import * as THREE from '/assets/scripts/three.module.js';
import { MOTHERBOARD_PCB_THICKNESS, MOTHERBOARD_SOCKET_DECK_HEIGHT } from '../spatial/geometry.js';
import { motherboardLandmarks } from '../simulator/motherboard-landmarks.js?v=20260817-pcieanchor10';
import { motherboardRamSlotLocalPositions, normalizeRamSlotCount } from '../simulator/ram-layout.js?v=20260817-pcieanchor10';
import { defaultMotherboardRamSlotCount, normalizeMotherboardFormFactor } from '../spatial/motherboard-standard.js?v=20260817-pcieanchor10';

export const COMPONENT_COLORS = Object.freeze({
  motherboard: 0x2563eb,
  gpu: 0x06b6d4,
  psu: 0xf87171,
  aircooler: 0x111318,
  ram: 0xa855f7,
  radiator: 0x14b8a6,
  fan: 0x25282d,
  generic: 0x64748b
});

export const COMPONENT_ACCENTS = Object.freeze({
  motherboard: 0x60a5fa,
  gpu: 0x67e8f9,
  psu: 0xfca5a5,
  aircooler: 0xd1d5db,
  ram: 0xd8b4fe,
  radiator: 0x5eead4,
  fan: 0xa78bfa,
  generic: 0xcbd5e1
});

function motherboardMaterial(color,roughness=0.8,metalness=0.1) {
  return new THREE.MeshStandardMaterial({color,roughness,metalness});
}

function addMotherboardDetailBox(group,x,y,z,material,px=0,py=0,pz=0,userData=null) {
  const mesh=new THREE.Mesh(new THREE.BoxGeometry(x,y,z),material);
  mesh.position.set(px,py,pz);
  mesh.castShadow=true;
  mesh.receiveShadow=true;
  if(userData)mesh.userData={...mesh.userData,...userData};
  group.add(mesh);
  return mesh;
}

function markMotherboardDecoration(group,kind='detail') {
  group.userData={...(group.userData||{}),sffCollisionIgnore:true,sffMotherboardDecoration:true,kind};
  return group;
}

function buildMotherboardPrimitive(W,L,resolvedColor,meta={}) {
  const group=new THREE.Group();
  const ff=normalizeMotherboardFormFactor(meta?.formFactor||'ITX');
  const landmarks=motherboardLandmarks({l:L,w:W},ff);
  const fallbackSlots=defaultMotherboardRamSlotCount(ff);
  const slotCount=normalizeRamSlotCount(meta?.ramSlots,fallbackSlots);
  const slots=motherboardRamSlotLocalPositions({l:L,w:W},ff,slotCount);

  // The PCB is the sole collision/containment mesh. Every raised motherboard feature below
  // is visual/relational detail and is explicitly ignored by calculateCleanBox().
  const pcbMaterial=motherboardMaterial(0x0d0d0d,0.92,0.08);
  const pcb=new THREE.Mesh(new THREE.BoxGeometry(W,MOTHERBOARD_PCB_THICKNESS,L),pcbMaterial);
  pcb.receiveShadow=true;
  pcb.castShadow=false;
  pcb.userData={sffMotherboardPcb:true,landmarkFrame:landmarks.frame};
  group.add(pcb);

  const edgeGeometry=new THREE.EdgesGeometry(pcb.geometry);
  const edgeLine=new THREE.LineSegments(
    edgeGeometry,
    new THREE.LineBasicMaterial({color:0x222222,transparent:true,opacity:0.95})
  );
  edgeLine.position.copy(pcb.position);
  edgeLine.userData={sffMotherboardEdge:true};
  group.add(edgeLine);

  const details=markMotherboardDecoration(new THREE.Group(),'motherboard_features');
  group.add(details);

  const ioMat=motherboardMaterial(0xc0c0c0,0.22,0.88);
  const portMat=motherboardMaterial(0x111111,0.90,0.10);
  const socketMat=motherboardMaterial(0xa0a0a0,0.32,0.75);
  const cavityMat=motherboardMaterial(0x0f0f0f,0.95,0.05);
  const frameMat=motherboardMaterial(0xd6d6d6,0.25,0.82);
  const ramBodyMat=motherboardMaterial(0x222222,0.85,0.15);
  const ramLatchMat=motherboardMaterial(0x6a6a6a,0.55,0.35);
  const pcieBodyMat=motherboardMaterial(0x151515,0.80,0.20);
  const pcieClipMat=motherboardMaterial(0x8a8a8a,0.50,0.40);
  const pcieInsertMat=motherboardMaterial(0x2a2a2a,0.88,0.08);

  // Rear I/O: physical LEFT edge of the cleaned front-view reference. Because the source
  // X axis maps to local -X to preserve chirality in the simulator's X/Z PCB plane, physical
  // LEFT is local +X here (and the DIMM/right edge is local -X).
  const halfW=W/2,halfL=L/2;
  const ioLength=Math.min(160,Math.max(50,L-10));
  const ioZ=halfL-(ioLength/2)-5;
  addMotherboardDetailBox(details,6,12,ioLength,ioMat,halfW+3,6,ioZ,{
    sffMotherboardFeature:true,kind:'rear_io_shroud'
  });
  [
    {span:12,height:10,z:ioLength*0.32},
    {span:12,height:8,z:ioLength*0.15},
    {span:14,height:12,z:ioLength*-0.05},
    {span:10,height:10,z:ioLength*-0.25},
    {span:16,height:6,z:ioLength*-0.42}
  ].forEach(port=>{
    addMotherboardDetailBox(
      details,1.2,port.height,port.span,portMat,
      halfW+6.2,6+(port.height*0.1),ioZ+port.z,
      {sffMotherboardFeature:true,kind:'rear_io_port'}
    );
  });

  // CPU socket: measured centre plus the cleaned 45 mm retention-frame visual.
  const cpu=new THREE.Group();
  cpu.position.set(landmarks.socketX,0,landmarks.socketZ);
  cpu.userData={sffMotherboardFeature:true,kind:'cpu_socket',landmarkFrame:landmarks.frame};
  details.add(cpu);
  addMotherboardDetailBox(cpu,45,1.5,45,socketMat,0,1.55,0);
  addMotherboardDetailBox(cpu,36.58,0.6,36.87,cavityMat,0,1.70,0);
  const rail=1.8,railHeight=0.9,railY=2.75;
  addMotherboardDetailBox(cpu,45,railHeight,rail,frameMat,0,railY,22.5-rail/2);
  addMotherboardDetailBox(cpu,45,railHeight,rail,frameMat,0,railY,-22.5+rail/2);
  addMotherboardDetailBox(cpu,rail,railHeight,45-(rail*2),frameMat,-22.5+rail/2,railY,0);
  addMotherboardDetailBox(cpu,rail,railHeight,45-(rail*2),frameMat,22.5-rail/2,railY,0);

  const lever=new THREE.Group();
  lever.position.set(-26,2.6,12);
  cpu.add(lever);
  addMotherboardDetailBox(lever,3,2.2,8,frameMat,0,0.2,0);
  const leverRod=new THREE.Mesh(new THREE.CylinderGeometry(0.9,0.9,22,12),motherboardMaterial(0xd0d0d0,0.30,0.70));
  leverRod.rotation.z=Math.PI/2;
  leverRod.position.set(-1.5,1,10);
  leverRod.castShadow=true;
  lever.add(leverRod);

  // DIMM sockets use the same measured slot centres consumed by RAM attachment.
  for(const slot of slots){
    const dimm=new THREE.Group();
    dimm.position.set(slot.x,0,slot.z);
    dimm.userData={
      sffMotherboardFeature:true,kind:'dimm_slot',slotIndex:slot.index,
      placementSource:slot.source,landmarkFrame:landmarks.frame
    };
    details.add(dimm);
    addMotherboardDetailBox(dimm,6,5,133.35,ramBodyMat,0,3.3,0);
    addMotherboardDetailBox(dimm,2,1.2,2,motherboardMaterial(0x000000,0.9,0.05),0,5.3,0);
    addMotherboardDetailBox(dimm,4.5,6,6.5,ramLatchMat,0,3.8,69.875);
    addMotherboardDetailBox(dimm,4.5,6,6.5,ramLatchMat,0,3.8,-69.875);
  }

  // Primary PCIe x16: 89 mm body with retention clip, universally 165 mm from board top.
  const pcie=new THREE.Group();
  pcie.position.set(landmarks.pcieCenterX,0,landmarks.pcieZ);
  pcie.userData={sffMotherboardFeature:true,kind:'pcie_x16',landmarkFrame:landmarks.frame};
  details.add(pcie);
  addMotherboardDetailBox(pcie,89,4,11.5,pcieBodyMat,0,2.8,0);
  addMotherboardDetailBox(pcie,5,6,5,pcieClipMat,-42,4,-2);
  addMotherboardDetailBox(pcie,2,3,8,pcieClipMat,-45.5,4.8,1);
  addMotherboardDetailBox(pcie,84,0.6,1.5,pcieInsertMat,0,4.6,0);

  group.userData={
    ...(group.userData||{}),
    sffMotherboardModel:true,
    landmarkFrame:landmarks.frame,
    formFactor:ff,
    ramSlotCount:slots.length
  };
  return {group,pcb,details,landmarks,slots};
}

function buildRamKitPrimitive(W,H,L,color,meta={}) {
  const group=new THREE.Group();
  const moduleCount=Math.max(1,Math.min(4,Math.round(Number(meta?.ramKitModules)||2)));
  const defaultPitch=10;
  const meshes=[];
  for(let i=0;i<moduleCount;i++){
    const module=solidBox(W,H,L,color);
    module.position.set((i-(moduleCount-1)/2)*defaultPitch,H/2,0);
    module.userData={sffRamModuleVisual:true,ramModuleIndex:i};
    group.add(module);meshes.push(module);
  }
  return {group,meshes,moduleCount};
}

function simpleMaterial(color) {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.72,
    metalness: 0.05,
    transparent: false,
    opacity: 1
  });
}

function solidBox(x, y, z, color) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(x, y, z), simpleMaterial(color));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function addSolid(group, x, y, z, color, px=0, py=0, pz=0) {
  const mesh=solidBox(Math.max(0.5,x),Math.max(0.5,y),Math.max(0.5,z),color);
  mesh.position.set(px,py,pz);
  group.add(mesh);
  return mesh;
}

function buildAirCoolerPrimitive(W,H,L,color,meta={}) {
  const group=new THREE.Group();
  const clearance=Number(meta?.ramClearance)||0;
  const clearanceKnown=!!meta?.ramClearanceKnown && !meta?.ramClearanceOpen && clearance>0 && clearance<H-2;
  const type=String(meta?.coolerType||'').toLowerCase();
  const isTopDown=/(top\s*down|low\s*profile|horizontal)/.test(type);
  const isDualTower=/(dual\s*tower|double\s*tower)/.test(type);
  const isTower=isDualTower || /(tower|single\s*tower)/.test(type);

  if(!clearanceKnown){
    const body=addSolid(group,W,H,L,color,0,H/2,0);
    return {group,body,clearanceVisual:null,shapeFamily:isTopDown?'top_down':(isTower?'tower':'box')};
  }

  // RAM clearance is compatibility data, not a literal mesh subtraction. The previous
  // sideways-U boolean-looking primitive made tower coolers visually implausible and still
  // collided with RAM because the conflict system saw the remaining fin pieces. Build a
  // recognizable family silhouette instead; RAM/cooler overlap is handled semantically by
  // the authoritative clearance value in ram-clearance.js.
  const localClearance=Math.max(4,Math.min(H-5,clearance-MOTHERBOARD_SOCKET_DECK_HEIGHT));
  const baseH=Math.min(5,Math.max(2.5,H*0.05));
  const baseW=Math.min(44,W*0.46), baseL=Math.min(44,L*0.46);
  addSolid(group,baseW,baseH,baseL,color,0,baseH/2,0);

  if(isTower){
    const finBottom=Math.max(localClearance,Math.min(H*0.38,H-12));
    const finH=Math.max(10,H-finBottom);
    const stemH=Math.max(4,finBottom-baseH);
    addSolid(group,Math.min(18,W*0.22),stemH,Math.min(28,L*0.28),color,0,baseH+stemH/2,0);
    if(isDualTower && L>65){
      const gap=Math.max(5,Math.min(12,L*0.08));
      const eachL=Math.max(18,(L-gap)/2);
      addSolid(group,W,finH,eachL,color,0,finBottom+finH/2,-(eachL+gap)/2);
      addSolid(group,W,finH,eachL,color,0,finBottom+finH/2,(eachL+gap)/2);
    }else{
      addSolid(group,W,finH,L,color,0,finBottom+finH/2,0);
    }
    return {group,body:null,clearanceVisual:{height:localClearance,source:'catalog_ram_clearance'},shapeFamily:isDualTower?'dual_tower':'tower'};
  }

  if(isTopDown){
    const slabBottom=Math.max(localClearance,Math.min(H*0.50,H-8));
    const slabH=Math.max(7,H-slabBottom);
    addSolid(group,W,slabH,L,color,0,slabBottom+slabH/2,0);
    // A single support/heatpipe mass on the side opposite the DIMM bank gives L12-style
    // coolers a readable low-profile silhouette without pretending the exact pipe geometry.
    const supportW=Math.max(7,Math.min(16,W*0.18));
    const supportH=Math.max(5,slabBottom-baseH);
    const supportX=-W/2+supportW/2+2;
    addSolid(group,supportW,supportH,Math.min(L*0.62,56),color,supportX,baseH+supportH/2,0);
    return {group,body:null,clearanceVisual:{height:localClearance,source:'catalog_ram_clearance'},shapeFamily:'top_down'};
  }

  const body=addSolid(group,W,H,L,color,0,H/2,0);
  return {group,body,clearanceVisual:{height:localClearance,source:'catalog_ram_clearance'},shapeFamily:'box'};
}

export function inferComponentMeta(trackType, label = '', dims = {}) {
  const text = String(label).toLowerCase();
  const meta = {};
  if (trackType === 'motherboard') {
    meta.formFactor = text.includes('micro') || text.includes('matx') ? 'MATX'
      : text.includes('atx') && !text.includes('itx') ? 'ATX'
      : text.includes('dtx') ? 'DTX' : 'ITX';
    meta.ramSlots = defaultMotherboardRamSlotCount(meta.formFactor);
  } else if (trackType === 'psu') {
    meta.formFactor = text.includes('sfx-l') || text.includes('sfx l') ? 'SFX-L'
      : text.includes('flex') ? 'FLEX'
      : text.includes('atx') && !text.includes('sfx') ? 'ATX' : 'SFX';
  } else if (trackType === 'radiator') {
    const match = text.match(/\b(120|140|240|280|360|420)\b/);
    meta.radiatorSize = match ? Number(match[1]) : Math.round(Number(dims.l || 0) / 120) * 120;
  }
  return meta;
}

export function createProceduralComponent({ trackType, l, w, h, label='', color=null, isComponent=true, componentMeta=null }) {
  const L=Number(l)||100, W=Number(w)||50, H=Number(h)||50;
  const resolvedColor = COMPONENT_COLORS[trackType] ?? color ?? COMPONENT_COLORS.generic;
  const resolvedMeta = { ...inferComponentMeta(trackType,label,{l:L,w:W,h:H}), ...(componentMeta||{}) };
  const wrapper = new THREE.Group();
  let mesh;

  if (trackType === 'motherboard') {
    const boardVisual=buildMotherboardPrimitive(W,L,resolvedColor,resolvedMeta);
    mesh=boardVisual.group;
    wrapper.motherboardCollisionMesh=boardVisual.pcb;
    wrapper.motherboardLandmarks=boardVisual.landmarks;
    wrapper.motherboardRamSlotCount=boardVisual.slots.length;
    wrapper.motherboardLandmarkFrame=boardVisual.landmarks.frame;
    wrapper.physicalLocalSize = [W,MOTHERBOARD_PCB_THICKNESS,L];
  } else if (trackType === 'gpu') {
    // X=GPU length, Y=GPU height, Z=GPU thickness.
    mesh = solidBox(L, H, W, resolvedColor);
    wrapper.physicalLocalSize = [L,H,W];
  } else if (trackType === 'psu') {
    // X=PSU length, Y=PSU height, Z=PSU depth.
    mesh = solidBox(L, W, H, resolvedColor);
    wrapper.physicalLocalSize = [L,W,H];
  } else if (trackType === 'aircooler') {
    // Wrapper origin is the CPU socket/base. When RAM clearance is known, use a composite
    // source-informed silhouette with a visible RAM-side cut-out instead of one opaque box.
    const coolerVisual=buildAirCoolerPrimitive(W,H,L,resolvedColor,resolvedMeta);
    mesh=coolerVisual.group;
    wrapper.ramClearanceVisual=coolerVisual.clearanceVisual;
    wrapper.coolerShapeFamily=coolerVisual.shapeFamily||'box';
    wrapper.physicalLocalSize = [W,H,L];
  } else if (trackType === 'ram') {
    // A RAM catalogue selection represents the maintained 2-DIMM kit convention. Height is
    // the source-authoritative compatibility metric; module length/depth remain visual UDIMM
    // envelopes. The two module meshes are re-spaced onto the active board's actual RAM-slot
    // count by the relational attachment layer.
    const ramVisual=buildRamKitPrimitive(W,H,L,resolvedColor,resolvedMeta);
    mesh=ramVisual.group;
    wrapper.ramModuleMeshes=ramVisual.meshes;
    wrapper.ramKitModules=ramVisual.moduleCount;
    wrapper.physicalLocalSize = [W,H,L];
  } else if (trackType === 'radiator') {
    mesh = solidBox(W, H, L, resolvedColor);
    wrapper.physicalLocalSize = [W,H,L];
  } else {
    mesh = solidBox(W, H, L, resolvedColor);
    wrapper.physicalLocalSize = [W,H,L];
  }

  wrapper.add(mesh);
  wrapper.uid = (trackType === 'motherboard' ? 'sffmobo_' : 'sff') + Date.now() + '_' + Math.random().toString(36).slice(2,7);
  wrapper.isComponent = !!isComponent;
  wrapper.isCase = !isComponent;
  wrapper.trackType = trackType;
  wrapper.nativeSpecs = { l:L, w:W, h:H };
  wrapper.totalModelHeight = trackType === 'motherboard' ? MOTHERBOARD_PCB_THICKNESS : H;
  wrapper.nativePhysicalHeight = wrapper.totalModelHeight;
  wrapper.componentMeta = resolvedMeta;
  wrapper.visualStyle = trackType === 'motherboard' ? 'motherboard_reference_v5' : 'solid_category_box';
  wrapper.categoryBodyColor = resolvedColor;
  wrapper.categoryColor = COMPONENT_ACCENTS[trackType] ?? resolvedColor;
  if (trackType === 'aircooler') { wrapper.isAirCooler = true; wrapper.socketQuarterTurn = 0; }
  if (trackType === 'ram') wrapper.isRamModule = true;
  return wrapper;
}
